/**
 * Browser-Side BWA-File-Parser.
 *
 * Brückt zwischen File-API und der pure-function-Parser im Core. Akzeptiert
 * .xlsx, .xls (via exceljs) — PDF wird bewusst noch nicht unterstützt
 * (siehe Hinweis im UI). Returnt das vom Core gelieferte Resultat plus
 * den Sheet-Namen, damit die UI ggf. mehrere Sheets zur Auswahl stellen
 * kann.
 */

import ExcelJS from 'exceljs';
import { financial } from '@tragfaehigkeit/core';

export type FileParseResult =
  | (financial.BwaParseResult & { readonly sheetName: string; readonly sheetCount: number })
  | { readonly status: 'unsupported_format'; readonly hint: string }
  | { readonly status: 'read_error'; readonly hint: string };

const XLSX_EXTENSIONS = ['.xlsx', '.xls', '.xlsm'];

function looksLikeXlsx(file: File): boolean {
  const name = file.name.toLowerCase();
  return XLSX_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function looksLikePdf(file: File): boolean {
  return file.name.toLowerCase().endsWith('.pdf');
}

export async function parseBwaFile(file: File): Promise<FileParseResult> {
  if (looksLikePdf(file)) {
    return {
      status: 'unsupported_format',
      hint: 'PDF-Import ist in Phase 2 (AP9b) geplant. Bitte exportieren Sie die BWA aus DATEV als XLSX oder geben Sie die Werte manuell ein.',
    };
  }
  if (!looksLikeXlsx(file)) {
    return {
      status: 'unsupported_format',
      hint: 'Nicht unterstütztes Format. Bitte XLSX oder XLS verwenden (DATEV-Export → „Excel-Datei").',
    };
  }

  let workbook: ExcelJS.Workbook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
  } catch (err) {
    return {
      status: 'read_error',
      hint:
        'Datei konnte nicht gelesen werden: ' +
        (err instanceof Error ? err.message : 'unbekannter Fehler'),
    };
  }

  // Wir parsen das erste Sheet — multi-sheet-Auswahl ist AP9b
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return { status: 'read_error', hint: 'Datei enthält keine lesbaren Sheets.' };
  }

  const rows: financial.ParsedRow[] = [];
  sheet.eachRow({ includeEmpty: true }, (row) => {
    const values: Array<string | number | null> = [];
    // exceljs nutzt 1-basierte Spalten — Index 0 ist immer null
    for (let c = 1; c <= row.cellCount; c += 1) {
      const cell = row.getCell(c).value;
      if (cell === null || cell === undefined) {
        values.push(null);
      } else if (typeof cell === 'number' || typeof cell === 'string') {
        values.push(cell);
      } else if (typeof cell === 'object' && 'result' in cell && cell.result !== undefined) {
        // formula cell
        const r = cell.result as unknown;
        values.push(typeof r === 'number' || typeof r === 'string' ? r : null);
      } else if (cell instanceof Date) {
        values.push(cell.toISOString());
      } else {
        values.push(null);
      }
    }
    rows.push({ values });
  });

  const result = financial.parseBwaSheet({ name: sheet.name, rows });
  return {
    ...result,
    sheetName: sheet.name,
    sheetCount: workbook.worksheets.length,
  };
}
